import AttachmentIcon from "@mui/icons-material/Attachment";
import { Box, IconButton, TextField } from "@mui/material";
import { ChangeEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import EmojiPicker from "../../../common/components/functional/emojiPicker/EmojiPicker";
import { insertTextAtSelection } from "../../../common/components/functional/emojiPicker/emojiPickerUtils";
import { ReactComponent as SendIcon } from "../../../common/icons/send-light.svg";
import { ATTACHMENT_ACCEPT } from "../attachments/constants";
import {
  MessagingComposerContainer,
  MessagingComposerPendingBar,
  MessagingComposerTitle,
  MessagingComposerTitleField,
  MessagingComposerToolbar,
} from "../styles";
import { shouldShowConversationTitle } from "./conversationTitle";
import MessageAttachmentChip from "./MessageAttachmentChip";

const COMPOSER_MAX_LENGTH = 10000;

type Props = {
  value: string;
  onChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onSend: () => void;
  sendingDisabled: boolean;
  inputDisabled: boolean;
  placeholder?: string;
  rows?: number;
  pendingFiles?: File[];
  onAddFiles?: (files: File[]) => void;
  onRemovePendingFile?: (index: number) => void;
  /**
   * Conversation topic. Hidden when empty.
   * Pass onTitleChange with titleLocked={false} to enable outgoing entry
   * before the first message is sent. After send, titleLocked must stay true.
   */
  title?: string;
  titleLocked?: boolean;
  onTitleChange?: (value: string) => void;
};

const toolbarIconButtonSx = { p: "7px" } as const;

const MessagingComposer = ({
  value,
  onChange,
  onSend,
  sendingDisabled,
  inputDisabled,
  placeholder,
  rows = 6,
  pendingFiles = [],
  onAddFiles,
  onRemovePendingFile,
  title,
  titleLocked = true,
  onTitleChange,
}: Props) => {
  const { t } = useTranslation(["messaging", "common"]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const titleEditable = Boolean(onTitleChange) && !titleLocked;
  const showTitle = shouldShowConversationTitle({
    title,
    locked: titleLocked,
    editingEnabled: Boolean(onTitleChange),
  });

  const emitChange = (nextValue: string) => {
    onChange({
      target: { value: nextValue },
    } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
  };

  const handleEmojiSelect = (emoji: string) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    const inserted = insertTextAtSelection({
      value,
      insert: emoji,
      start,
      end,
      maxLength: COMPOSER_MAX_LENGTH,
    });
    if (!inserted) return;

    emitChange(inserted.value);
    setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(inserted.caret, inserted.caret);
    }, 0);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length) onAddFiles?.(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <MessagingComposerContainer>
      {showTitle &&
        (titleEditable ? (
          <MessagingComposerTitleField
            size="small"
            variant="outlined"
            fullWidth
            value={title ?? ""}
            placeholder={t("messaging:composerTitlePlaceholder")}
            onChange={(event) => onTitleChange?.(event.target.value)}
            inputProps={{
              "aria-label": t("messaging:conversationTitle"),
              "data-testid": "conversation-title",
            }}
          />
        ) : (
          <MessagingComposerTitle data-testid="conversation-title">
            {title}
          </MessagingComposerTitle>
        ))}
      <Box
        sx={{
          flex: "1 1 auto",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <TextField
          variant="standard"
          multiline
          fullWidth
          value={value}
          onChange={onChange}
          disabled={inputDisabled}
          placeholder={placeholder}
          rows={rows}
          inputRef={inputRef}
          sx={{
            height: "100%",
            boxSizing: "border-box",
            "& .MuiInputBase-input": {
              p: "8px 16px",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            },
          }}
          InputProps={{
            disableUnderline: true,
          }}
          inputProps={{ maxLength: COMPOSER_MAX_LENGTH }}
        />
      </Box>
      {pendingFiles.length > 0 && (
        <MessagingComposerPendingBar data-testid="composer-pending-attachments">
          {pendingFiles.map((file, index) => (
            <MessageAttachmentChip
              key={`${file.name}-${file.size}-${index}`}
              compact
              attachment={{
                id: `${file.name}-${index}`,
                filename: file.name,
                size: file.size,
                contentType: file.type || "application/octet-stream",
                status: "local",
              }}
              onRemove={
                inputDisabled ? undefined : () => onRemovePendingFile?.(index)
              }
            />
          ))}
        </MessagingComposerPendingBar>
      )}
      <MessagingComposerToolbar>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={ATTACHMENT_ACCEPT}
            disabled={inputDisabled}
            onChange={handleFileChange}
          />
          <IconButton
            aria-label={t("common:addAttachment")}
            disabled={inputDisabled}
            sx={toolbarIconButtonSx}
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachmentIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <EmojiPicker
            onSelect={handleEmojiSelect}
            disabled={inputDisabled}
            buttonSx={toolbarIconButtonSx}
          />
        </Box>
        <IconButton
          aria-label={t("common:send")}
          disabled={sendingDisabled}
          onClick={onSend}
          sx={{
            ...toolbarIconButtonSx,
            "& svg": { width: 22, height: 22 },
            "& path": { stroke: "currentColor", strokeOpacity: 1 },
          }}
        >
          <SendIcon />
        </IconButton>
      </MessagingComposerToolbar>
    </MessagingComposerContainer>
  );
};

export default MessagingComposer;
